import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', GREEN = '#2ECC71', TEAL = '#3B82F6';

/* Walks from step 0 up to stepIndex, folding in any snapshot/var overrides
   along the way — so an array that hasn't changed since step 2 still shows
   its step-2 value at step 5 without every step needing to repeat it. */
const stateAtStep = (visual, stepIndex) => {
  const arrays = {};
  (visual.arrays || []).forEach(a => { arrays[a.label] = a.values; });
  let tree = visual.tree ? visual.tree.value : undefined;
  let trie = visual.trie ? visual.trie.value : undefined;
  let vars = {};
  for (let s = 0; s <= stepIndex; s++) {
    const st = visual.steps[s];
    if (st.snapshots) Object.entries(st.snapshots).forEach(([k, v]) => {
      if (k === 'tree') tree = v; else if (k === 'trie') trie = v; else arrays[k] = v;
    });
    if (st.vars) vars = { ...vars, ...st.vars };
  }
  return { arrays, vars, tree, trie };
};

/* A graph: arbitrary nodes and edges (can cycle, unlike TreeRow/TrieRow), laid out on a
   circle so positions are computed from trigonometry rather than guessed by hand.
   highlight is an array of NODE VALUES; highlightEdges is an array of [a, b] pairs. */
const GraphRow = ({ label, nodes, edges, directed, highlight, visited, highlightEdges = [] }) => {
  const n = nodes.length;
  const R = 78, cx = 100, cy = 100;
  const pos = nodes.map((_, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) };
  });
  const nodeIndex = val => nodes.indexOf(val);
  const isEdgeHighlighted = (a, b) => highlightEdges.some(([x, y]) => (x === a && y === b) || (!directed && x === b && y === a));
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
        <svg width="200" height="200" viewBox="0 0 200 200">
          {directed && (
            <defs>
              <marker id={`avGraphArrow-${label}`} markerWidth="8" markerHeight="8" refX="14" refY="4" orient="auto">
                <polygon points="0 0, 8 4, 0 8" style={{ fill: 'var(--muted)' }} />
              </marker>
            </defs>
          )}
          {edges.map(([a, b, weight], i) => {
            const ai = nodeIndex(a), bi = nodeIndex(b);
            if (ai === -1 || bi === -1) return null;
            const hl = isEdgeHighlighted(a, b);
            const mx = (pos[ai].x + pos[bi].x) / 2, my = (pos[ai].y + pos[bi].y) / 2;
            return (
              <g key={i}>
                <line
                  x1={pos[ai].x} y1={pos[ai].y} x2={pos[bi].x} y2={pos[bi].y}
                  style={{ stroke: hl ? V1 : 'var(--border-strong)' }}
                  strokeWidth={hl ? 3 : 1.5}
                  markerEnd={directed ? `url(#avGraphArrow-${label})` : undefined}
                />
                {weight !== undefined && (
                  <>
                    <circle cx={mx} cy={my} r="9" style={{ fill: 'var(--bg2)', stroke: hl ? V1 : 'var(--border)' }} strokeWidth="1" />
                    <text x={mx} y={my + 3} textAnchor="middle" fontSize="9" fontWeight="700" style={{ fill: hl ? V1 : 'var(--muted)', fontFamily: 'monospace' }}>{weight}</text>
                  </>
                )}
              </g>
            );
          })}
          {nodes.map((val, i) => {
            const isActive = highlight && highlight.includes(val);
            const isVisited = visited && visited.includes(val);
            return (
              <g key={val}>
                <circle cx={pos[i].x} cy={pos[i].y} r="15" style={{ fill: isActive ? V1 : 'var(--bg3)', stroke: isActive ? V1 : isVisited ? GREEN : 'var(--border)' }} strokeWidth="1.5" />
                <text x={pos[i].x} y={pos[i].y + 4.5} textAnchor="middle" fontSize="12" fontWeight="800" style={{ fill: isActive ? '#fff' : 'var(--text)', fontFamily: "'Inter',sans-serif" }}>{val}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

/* A static tree (usually) with per-step highlight/visited coloring —
   used by Tree-pattern problems where a flat array row can't show parent/child shape. */
const TreeRow = ({ label, node, highlight, visited }) => {
  if (!node) {
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <span style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'monospace' }}>empty tree</span>
      </div>
    );
  }
  const TreeNode = ({ n }) => {
    if (!n) return <span style={{ color: 'var(--muted)', fontSize: 11.5, fontFamily: 'monospace' }}>null</span>;
    const isActive = highlight && highlight.includes(n.val);
    const isVisited = visited && visited.includes(n.val);
    return (
      <div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 42, height: 42, padding: '0 10px', borderRadius: 10,
          background: isActive ? `linear-gradient(135deg,${V1},${V2})` : isVisited ? 'var(--bg3)' : 'var(--bg3)',
          border: `1.5px solid ${isActive ? V1 : isVisited ? GREEN : 'var(--border)'}`,
          color: isActive ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 15,
          fontFamily: "'Inter',sans-serif", transition: 'all 0.2s ease',
        }}>{n.val}</span>
        {(n.left || n.right) && (
          <div style={{ marginLeft: 18, paddingLeft: 16, marginTop: 10, borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {n.left && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: V2, marginTop: 12, flexShrink: 0 }}>L</span>
                <TreeNode n={n.left} />
              </div>
            )}
            {n.right && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: TEAL, marginTop: 12, flexShrink: 0 }}>R</span>
                <TreeNode n={n.right} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  return (
    <div style={{ marginBottom: 14, overflowX: 'auto' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <TreeNode n={node} />
    </div>
  );
};

/* A trie: unlike TreeRow's binary left/right, a node can branch into many children,
   each reached by a character. highlight is an array of PATH STRINGS (prefixes from
   the root) rather than values, since the same character can appear at many nodes. */
const TrieRow = ({ label, node, highlight }) => {
  if (!node) {
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <span style={{ color: 'var(--muted)', fontSize: 13, fontFamily: 'monospace' }}>empty trie</span>
      </div>
    );
  }
  const TrieNode = ({ n, ch, path }) => {
    const active = highlight && highlight.includes(path);
    const children = n.children ? Object.entries(n.children) : [];
    return (
      <div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          minWidth: 36, height: 36, padding: '0 8px', borderRadius: 9,
          background: active ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
          border: `1.5px solid ${active ? V1 : 'var(--border)'}`,
          color: active ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 13,
          fontFamily: "'Fira Code','Consolas',monospace",
        }}>
          {ch}
          {n.isEnd && (
            <span style={{ position: 'absolute', bottom: -4, right: -4, width: 10, height: 10, borderRadius: '50%', background: GREEN, border: '2px solid var(--bg2)' }} />
          )}
        </span>
        {children.length > 0 && (
          <div style={{ marginLeft: 16, paddingLeft: 14, marginTop: 9, borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 9 }}>
            {children.map(([c, child]) => <TrieNode key={c} n={child} ch={c} path={path + c} />)}
          </div>
        )}
      </div>
    );
  };
  return (
    <div style={{ marginBottom: 14, overflowX: 'auto' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <TrieNode n={node} ch="•" path="" />
    </div>
  );
};

/* highlight can be a plain array (all "primary") or { primary: [...], secondary: [...] }
   — secondary gets a distinct color so two roles (e.g. "buy day" vs "sell day") read apart. */
const ArrayRow = ({ label, values, highlight }) => {
  const primary = Array.isArray(highlight) ? highlight : (highlight?.primary || []);
  const secondary = Array.isArray(highlight) ? [] : (highlight?.secondary || []);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {values.map((v, i) => {
          const isPrimary = primary.includes(i);
          const isSecondary = secondary.includes(i);
          const active = isPrimary || isSecondary;
          const color = isSecondary ? TEAL : V1;
          return (
            <div key={i} style={{
              width: 46, height: 46, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800, fontFamily: "'Inter',sans-serif",
              background: isPrimary ? `linear-gradient(135deg,${V1},${V2})` : isSecondary ? `linear-gradient(135deg,${TEAL},#2563EB)` : 'var(--bg3)',
              color: active ? '#fff' : (v === null ? 'var(--muted)' : 'var(--text)'),
              border: `1.5px solid ${active ? color : 'var(--border)'}`, transition: 'all 0.2s ease',
            }}>
              {v === null || v === undefined ? '·' : v}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ApproachVisualizer = ({ visual }) => {
  const [step, setStep] = useState(0);
  const cur = visual.steps[step];
  const { arrays, vars, tree, trie } = stateAtStep(visual, step);

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px',
      background: 'var(--bg2)', marginBottom: 18,
    }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: V2, marginBottom: 14 }}>
        Watch it happen
      </div>

      {(visual.arrays || []).map(a => (
        <ArrayRow key={a.label} label={a.label} values={arrays[a.label]} highlight={cur.highlights && cur.highlights[a.label]} />
      ))}

      {visual.tree && (
        <TreeRow label={visual.tree.label} node={tree} highlight={cur.highlights && cur.highlights.tree} visited={cur.visited} />
      )}

      {visual.trie && (
        <TrieRow label={visual.trie.label} node={trie} highlight={cur.highlights && cur.highlights.trie} />
      )}

      {visual.graph && (
        <GraphRow
          label={visual.graph.label} nodes={visual.graph.nodes} edges={visual.graph.edges} directed={visual.graph.directed}
          highlight={cur.highlights && cur.highlights.graph} visited={cur.visited} highlightEdges={cur.highlightEdges}
        />
      )}

      {Object.keys(vars).length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0 14px' }}>
          {visual.varNames.filter(n => vars[n] !== undefined).map(name => (
            <div key={name} style={{
              padding: '6px 12px', borderRadius: 9, background: 'var(--bg3)', border: '1px solid var(--border)',
              fontSize: 12.5, fontFamily: 'monospace',
            }}>
              <span style={{ color: 'var(--muted)' }}>{name} = </span>
              <span style={{ color: V2, fontWeight: 700 }}>{vars[name]}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {cur.phase && (
          <span style={{
            fontSize: 10.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999,
            background: `${V1}18`, color: V1, whiteSpace: 'nowrap',
          }}>{cur.phase}</span>
        )}
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text)' }}>{cur.note}</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={navBtn(step === 0)}>← Prev</button>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Step {step + 1} of {visual.steps.length}</span>
        <button onClick={() => setStep(s => Math.min(visual.steps.length - 1, s + 1))} disabled={step === visual.steps.length - 1} style={navBtn(step === visual.steps.length - 1)}>Next →</button>
      </div>
    </div>
  );
};

const navBtn = disabled => ({
  padding: '7px 14px', borderRadius: 9, border: `1.5px solid ${disabled ? 'var(--border)' : V1}`,
  background: disabled ? 'transparent' : V1, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  fontFamily: "'Satoshi','DM Sans',sans-serif",
});

export default ApproachVisualizer;
